import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { contentWidthStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";
import { resolveColor } from "@insite/mobius/utilities";

interface OwnProps extends WidgetProps {
    extendedStyles?: MainHeaderContainerStyles;
}

export interface MainHeaderContainerStyles {
    fullWidthWrapper?: GridContainerProps;
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    logoGridItem?: InjectableCss;
    searchBarGridItem?: InjectableCss;
    userGridItem?: GridItemProps;
    cartGridItem?: InjectableCss;
    mobileSearchContainer?: GridContainerProps;
    mobileSearchBarGridItem?: GridItemProps;
    navigationGridItem?: InjectableCss;
}

export const mainHeaderContainerStyles: MainHeaderContainerStyles = {
    fullWidthWrapper: {
        gap: 0,
        css: css`
            border-top: 2px solid ${getColor("common.background")};
            border-bottom: 2px solid ${getColor("common.background")};
            background-color: ${getColor("secondary.main")};

            > ${GridOffset} {
                width: 100%;
            }
        `,
    },
    wrapper: {
        css: css`
            ${contentWidthStyles()}
            width: 100%;
            background-color: ${getColor("common.background")};
        `,
    },
    container: {
        gap: 0,
    },
    navigationGridItem: {
        css: css`
            display: flex;
            align-items: center;
            position: relative;
            background-color: ${getColor("secondary.main")};

            &::before {
                content: "";
                position: absolute;
                top: 0;
                right: -15px;
                width: 30px;
                height: 100%;
                background: ${getColor("secondary.main")};
                transform: skewX(17deg);
            }
        `,
    },
    logoGridItem: {
        css: css`
            display: flex;
            align-items: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["1", null, "inherit", null], properties: ["flex"] },
                        {
                            values: ["15px 5px 15px 30px", "15px 10px 15px 50px", "15px 30px 15px 50px", null],
                            properties: ["padding"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    searchBarGridItem: {
        css: css`
            align-items: center;
            padding: 10px 20px;
            flex: 1;

            > div {
                width: 100%;
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["none", null, "flex", null, null], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    userGridItem: {
        align: "middle",
        css: css`
            flex-basis: inherit !important;
            max-width: inherit !important;

            > div {
                text-align: right;
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0", null, null, "10px 20px", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    cartGridItem: {
        css: css`
            display: flex;
            align-items: center;
            position: relative;
            padding-left: 10px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["0", null, null, resolveColor("secondary.main", theme), null],
                            properties: ["background"],
                        },
                        { values: ["20px", "30px", null, null, null], properties: ["padding-right"] },
                    ],
                    mediaOptions.min,
                )}

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: -15px;
                width: 30px;
                height: 100%;
                background: ${getColor("secondary.main")};
                transform: skewX(-17deg);

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["none", null, null, "block", null], properties: ["display"] }],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    mobileSearchContainer: {
        gap: 0,
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["8px", null, "20px", null, null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    mobileSearchBarGridItem: {
        width: 12,
        align: "middle",
    },
};

const StoreMainHeaderContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(mainHeaderContainerStyles, extendedStyles));
    return (
        <div>
            <GridContainer {...styles.fullWidthWrapper}>
                <StyledWrapper {...styles.wrapper}>
                    <GridContainer {...styles.container}>
                        <StyledWrapper {...styles.navigationGridItem}>
                            <Zone zoneName="Navigation" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.logoGridItem}>
                            <Zone zoneName="LogoDesktop" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.searchBarGridItem}>
                            <Hidden below="sm">
                                <Zone zoneName="SearchBarDesktop" contentId={id} />
                            </Hidden>
                        </StyledWrapper>
                        <GridItem {...styles.userGridItem}>
                            <Hidden below="sm">
                                <Zone zoneName="User" contentId={id} />
                            </Hidden>
                        </GridItem>
                        <StyledWrapper {...styles.cartGridItem}>
                            <Zone zoneName="Cart" contentId={id} />
                        </StyledWrapper>
                    </GridContainer>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <Hidden above="sm">
                        <GridContainer {...styles.mobileSearchContainer}>
                            <GridItem {...styles.mobileSearchBarGridItem}>
                                <Zone zoneName="MobileSearch" contentId={id} />
                            </GridItem>
                        </GridContainer>
                    </Hidden>
                </StyledWrapper>
            </GridContainer>
        </div>
    );
};

const storeMainHeaderContainer: WidgetModule = {
    component: StoreMainHeaderContainer,
    definition: {
        group: "Header",
        isSystem: true,
    },
};

export default storeMainHeaderContainer;
